import { createFileRoute } from '@tanstack/react-router';
import { Badge } from '@workspace/ui/components/badge';
import { Button } from '@workspace/ui/components/button';
import { Checkbox } from '@workspace/ui/components/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@workspace/ui/components/collapsible';
import { toast } from '@workspace/ui/components/sonner';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  Copy,
  Loader2,
  ShieldCheck,
  Terminal,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { useAuth } from '~/auth/use-auth';
import { LoginLayout } from '~/components/layouts/login-layout';
import { API_URL } from '~/config/environment';
import { useSessionScopes } from '~/hooks/use-session-scopes';
import { m } from '~/paraglide/messages.js';
import { createApiKey } from '../dev/-api/api';
import {
  filterPresets,
  filterScopeGroups,
  SCOPE_GROUPS,
  type ScopeGroup,
  USE_CASE_PRESETS,
  type UseCasePreset,
} from '../dev/-config/scope-catalog';
import { CLI_AUTH_SUCCESS_KEY } from './-config/permissions';

type AuthState =
  | 'invalid_params'
  | 'select_use_case'
  | 'select_scopes'
  | 'creating'
  | 'sending'
  | 'success'
  | 'error';

export const Route = createFileRoute('/_authenticated/cli/auth')({
  validateSearch: (
    search: Record<string, string>,
  ): { port?: number; nonce?: string } => {
    const port = Number(search.port);
    return {
      port: Number.isFinite(port) ? port : undefined,
      nonce: search.nonce || undefined,
    };
  },
  component: CliAuthPage,
});

function isValidPort(port: number | undefined): port is number {
  return port !== undefined && port >= 1024 && port <= 65535;
}

function PresetCard({
  preset,
  isSelected,
  onSelect,
}: {
  preset: UseCasePreset;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const Icon = preset.icon;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`group flex items-start gap-3 rounded-lg border p-3 text-left transition-all duration-150 ${
        isSelected
          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
          : 'border-border hover:border-primary/40 hover:bg-muted/50'
      }`}
      aria-pressed={isSelected}
    >
      <div
        className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors ${
          isSelected
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary'
        }`}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-foreground">
            {preset.label}
          </span>
          {preset.scopes.length > 0 && (
            <Badge variant="secondary" className="shrink-0 text-[10px]">
              {preset.scopes.length} scopes
            </Badge>
          )}
        </div>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
          {preset.description}
        </p>
      </div>
    </button>
  );
}

function ScopeGroupSection({
  group,
  selectedScopes,
  onToggle,
}: {
  group: ScopeGroup;
  selectedScopes: Set<string>;
  onToggle: (scope: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const Icon = group.icon;
  const count = group.scopes.filter((s) => selectedScopes.has(s.key)).length;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between px-3 py-2.5 text-sm transition-colors hover:bg-muted/50">
        <div className="flex items-center gap-2.5">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-foreground">{group.label}</span>
          {count > 0 && (
            <Badge
              variant="default"
              className="h-5 min-w-5 justify-center px-1.5 text-[10px]"
            >
              {count}
            </Badge>
          )}
        </div>
        <ChevronRight
          className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-1 pb-2 pl-9 pr-3">
          {group.scopes.map((scope) => (
            <label
              key={scope.key}
              htmlFor={`cli-scope-${scope.key}`}
              className="flex cursor-pointer items-start gap-2.5 rounded-md px-2 py-1.5 transition-colors hover:bg-muted/50"
            >
              <Checkbox
                id={`cli-scope-${scope.key}`}
                checked={selectedScopes.has(scope.key)}
                onCheckedChange={() => onToggle(scope.key)}
                className="mt-0.5"
                aria-label={scope.label}
              />
              <div className="min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm text-foreground">{scope.label}</span>
                  <code className="font-mono text-[11px] text-muted-foreground">
                    {scope.key}
                  </code>
                </div>
                <p className="text-xs text-muted-foreground">
                  {scope.description}
                </p>
              </div>
            </label>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function CliAuthPage() {
  const { port, nonce } = Route.useSearch();
  const { user, updateMetadata } = useAuth();

  const paramsValid =
    isValidPort(port) && typeof nonce === 'string' && nonce.length > 0;

  const [state, setState] = useState<AuthState>(
    paramsValid ? 'select_use_case' : 'invalid_params',
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fallbackCommand, setFallbackCommand] = useState<string | null>(null);

  const sessionScopes = useSessionScopes();
  const availableGroups = useMemo(
    () => filterScopeGroups(SCOPE_GROUPS, sessionScopes),
    [sessionScopes],
  );
  const availablePresets = useMemo(
    () => filterPresets(USE_CASE_PRESETS, sessionScopes),
    [sessionScopes],
  );

  const defaultPresetIds = useMemo(
    () =>
      new Set(
        availablePresets.filter((p) => p.id !== 'custom').map((p) => p.id),
      ),
    [availablePresets],
  );
  const defaultScopes = useMemo(() => {
    const merged = new Set<string>();
    for (const p of availablePresets) {
      if (p.id !== 'custom') {
        for (const s of p.scopes) merged.add(s);
      }
    }
    return merged;
  }, [availablePresets]);

  const [selectedPresets, setSelectedPresets] =
    useState<Set<string>>(defaultPresetIds);
  const [selectedScopes, setSelectedScopes] =
    useState<Set<string>>(defaultScopes);

  const handleTogglePreset = useCallback(
    (presetId: string) => {
      setSelectedPresets((prev) => {
        const next = new Set(prev);
        if (next.has(presetId)) {
          next.delete(presetId);
        } else {
          next.add(presetId);
        }
        const merged = new Set<string>();
        for (const id of next) {
          const preset = availablePresets.find((p) => p.id === id);
          if (preset) {
            for (const s of preset.scopes) merged.add(s);
          }
        }
        setSelectedScopes(merged);
        return next;
      });
    },
    [availablePresets],
  );

  const handleToggleScope = useCallback((scope: string) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  }, []);

  const handleAuthorize = useCallback(async () => {
    if (!isValidPort(port) || !nonce) return;
    if (selectedScopes.size === 0) return;

    setState('creating');
    setErrorMessage(null);

    try {
      const result = await createApiKey({
        name: `CLI ${new Date().toISOString().slice(0, 10)}`,
        scopes: [...selectedScopes],
        domains: ['cli://localhost'],
        expiration: 'never',
      });

      setState('sending');

      try {
        const response = await fetch(`http://127.0.0.1:${port}/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            api_key: result.secret_key,
            nonce,
            api_url: API_URL,
          }),
        });

        if (!response.ok) {
          const cmd = `bloque login --api-key ${result.secret_key}`;
          setFallbackCommand(cmd);
          setErrorMessage(m['cli_auth.error_description']());
          setState('error');
          return;
        }

        localStorage.setItem(CLI_AUTH_SUCCESS_KEY, Date.now().toString());
        updateMetadata({ agent_connected: true });
        setState('success');
        setTimeout(() => window.close(), 2000);
      } catch {
        const cmd = `bloque login --api-key ${result.secret_key}`;
        setFallbackCommand(cmd);
        setErrorMessage(m['cli_auth.callback_failed']());
        setState('error');
      }
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : m['cli_auth.error_description'](),
      );
      setState('error');
    }
  }, [port, nonce, selectedScopes, selectedPresets, updateMetadata]);

  const copyFallback = useCallback(async () => {
    if (!fallbackCommand) return;
    try {
      await navigator.clipboard.writeText(fallbackCommand);
      toast.success(m['common.copied_to_clipboard']());
    } catch {
      toast.error('Failed to copy command');
    }
  }, [fallbackCommand]);

  if (state === 'invalid_params') {
    return (
      <LoginLayout>
        <div className="flex min-h-[80vh] w-full flex-col items-center justify-center">
          <div className="w-full max-w-md space-y-6 text-center">
            <div className="flex items-center justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10">
                <AlertCircle className="h-8 w-8 text-destructive" />
              </div>
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold text-foreground">
                {m['cli_auth.error_title']()}
              </h1>
              <p className="text-muted-foreground">
                {m['cli_auth.invalid_request']()}
              </p>
            </div>
          </div>
        </div>
      </LoginLayout>
    );
  }

  if (state === 'success') {
    return (
      <LoginLayout>
        <div className="flex min-h-[80vh] w-full flex-col items-center justify-center">
          <div className="w-full max-w-md space-y-6 text-center">
            <div className="flex items-center justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-green-500/10">
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold text-foreground">
                {m['cli_auth.success_title']()}
              </h1>
              <p className="text-muted-foreground">
                {m['cli_auth.success_description']()}
              </p>
            </div>
          </div>
        </div>
      </LoginLayout>
    );
  }

  return (
    <LoginLayout>
      <div className="flex min-h-[80vh] w-full flex-col items-center justify-center">
        <div className="w-full max-w-lg space-y-6">
          <div className="flex items-center justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
              <ShieldCheck className="h-8 w-8 text-primary" />
            </div>
          </div>

          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-semibold text-foreground">
              {m['cli_auth.title']()}
            </h1>
            <p className="text-muted-foreground">
              {m['cli_auth.description']()}
            </p>
          </div>

          {user && (
            <div className="rounded-xl border border-border bg-muted/50 p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                  {user.profile.first_name?.[0]}
                  {user.profile.last_name?.[0]}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">
                    {user.profile.full_name}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {user.profile.email}
                  </p>
                </div>
              </div>
            </div>
          )}

          {state === 'select_use_case' && (
            <div className="space-y-4">
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">
                  {m['cli_auth.select_use_case']()}
                </p>
                <p className="text-xs text-muted-foreground">
                  {m['cli_auth.select_use_case_hint']()}
                </p>
              </div>
              <div className="space-y-2">
                <div className="grid gap-2">
                  {availablePresets
                    .filter((p) => p.id !== 'custom')
                    .map((preset) => (
                      <PresetCard
                        key={preset.id}
                        preset={preset}
                        isSelected={selectedPresets.has(preset.id)}
                        onSelect={() => handleTogglePreset(preset.id)}
                      />
                    ))}
                </div>
                {availablePresets
                  .filter((p) => p.id === 'custom')
                  .map((preset) => (
                    <PresetCard
                      key={preset.id}
                      preset={preset}
                      isSelected={selectedPresets.has('custom')}
                      onSelect={() => handleTogglePreset('custom')}
                    />
                  ))}
              </div>
              <Button
                onClick={() => setState('select_scopes')}
                disabled={selectedPresets.size === 0}
                className="w-full py-6 font-semibold"
              >
                {m['developer.api_keys.wizard.continue']()}
              </Button>
            </div>
          )}

          {state === 'select_scopes' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">
                    {m['cli_auth.review_permissions']()}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {m['cli_auth.review_permissions_hint']()}
                  </p>
                </div>
                <Badge variant="outline">{selectedScopes.size} selected</Badge>
              </div>

              {selectedScopes.size === 0 && (
                <p
                  className="text-sm font-medium text-destructive"
                  role="alert"
                >
                  {m['developer.api_keys.wizard.scopes_empty_error']()}
                </p>
              )}

              <div className="overflow-hidden rounded-xl border border-border">
                {availableGroups.map((group, i) => (
                  <div
                    key={group.id}
                    className={i > 0 ? 'border-t border-border' : ''}
                  >
                    <ScopeGroupSection
                      group={group}
                      selectedScopes={selectedScopes}
                      onToggle={handleToggleScope}
                    />
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setState('select_use_case')}
                  className="gap-1.5"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {m['common.back']()}
                </Button>
                <Button
                  onClick={handleAuthorize}
                  disabled={selectedScopes.size === 0}
                  className="flex flex-1 items-center justify-center gap-2 py-6 font-semibold"
                >
                  {m['cli_auth.authorize']()}
                </Button>
              </div>
            </div>
          )}

          {(state === 'creating' || state === 'sending') && (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                {state === 'creating'
                  ? m['cli_auth.creating_key']()
                  : m['cli_auth.sending_to_cli']()}
              </p>
            </div>
          )}

          {state === 'error' && (
            <div className="space-y-4">
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center">
                <p className="text-sm text-destructive">
                  {errorMessage || m['cli_auth.error_description']()}
                </p>
              </div>

              {fallbackCommand && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    {m['cli_auth.fallback_title']()}
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex flex-1 items-center gap-2 overflow-hidden rounded-md border bg-zinc-950 p-2.5 font-mono text-xs text-green-400">
                      <Terminal className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
                      <span className="break-all">{fallbackCommand}</span>
                    </div>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={copyFallback}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setState('select_scopes');
                    setErrorMessage(null);
                    setFallbackCommand(null);
                  }}
                  className="gap-1.5"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {m['common.back']()}
                </Button>
                <Button onClick={handleAuthorize} className="flex-1">
                  {m['cli_auth.error_retry']()}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </LoginLayout>
  );
}
